import type { ReactiveController, ReactiveControllerHost } from 'lit';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { Viewport } from '../../core/bus/whiteboard-bus.js';
import { isInsideEditable } from '../../core/dom/editable.js';
import { isEditableTarget } from '../../core/viewport/viewport.controller.js';
import { fitImageWorldSize } from './image-defaults.factory.js';
import { decidePasteRoute } from './paste-route.js';
import type { ImageSource, ImageUploadHandler, PasteChoice, PasteChoiceHandler } from './image.interface.js';

/**
 * Lit ReactiveController that turns native browser paste events into
 * `place/image` UiActions on the whiteboard bus (PRD-F4 §T5 / Variante C).
 *
 * Lifecycle:
 *   - The constructor only stores references; it does NOT call
 *     `host.addController` (FC10 — the element wires the controller
 *     explicitly to keep paste handling under owner control).
 *   - `hostConnected` registers a single `paste` listener on `window`,
 *     guarded against double-registration.
 *   - `hostDisconnected` removes the listener.
 *
 * Dispatch:
 *   - When an upload handler is injected (O2D.W-PRD-042 §TR3), the clipboard
 *     `File` is uploaded and the returned asset URL is placed as `src` — the
 *     base64 data-URL never enters the board state. A rejected upload is logged
 *     and places NOTHING (no silent base64 fallback, that would defeat the seam).
 *   - Without a handler, the blob is read via `FileReader.readAsDataURL`; on
 *     `onload` we synchronously call
 *     `bus.uiActions$.next({type:'place/image', worldX, worldY, dataUrl})`.
 *   - The clipboard is read synchronously inside the event (it is unreadable
 *     afterwards); only the optional choice below awaits.
 *   - Only the FIRST image item in `clipboardData.items` is handled
 *     (Single-Image-First — FC16); subsequent items are ignored.
 *   - Pastes that belong to an editor are ignored entirely (FC11): an
 *     editable target (`<input>`, `<textarea>`, contentEditable), a target
 *     INSIDE an editable region (e.g. the textbox editor's non-editable
 *     link-icon widget), or a paste an editor already handled
 *     (`defaultPrevented` — ProseMirror prevents but never stops the event).
 *     Anke, 2026-10-02: text pasted into a textbox sometimes ALSO landed on
 *     the board as an image. The trigger was not reproduced (a live Ctrl+V
 *     always targeted editable text); these guards make the board stay out of
 *     any paste an editor took, wherever its target lay.
 *   - The board's OWN marker (`o2d-whiteboard/v1:`, see clipboard-payload.ts)
 *     is recognised first, even when the clipboard also holds image/png (a copied
 *     image object carries both since 2026-10-28): the objects are pasted as
 *     before, with no question and no upload. The routing lives in paste-route.ts.
 *   - Text AND image in the clipboard (PowerPoint, Word, Excel): when the host
 *     supplied a `PasteChoiceHandler`, the user picks image or text (or
 *     nothing); one question at a time, and while it is open every Ctrl+V onto
 *     the board is swallowed. Without a handler the image wins.
 *   - Only text: `clipboard/paste-text`. Neither: `clipboard/paste`, so Ctrl+V
 *     still pastes copied board objects (a no-op when nothing was copied).
 *     The keyboard-shortcuts controller deliberately does NOT bind Ctrl+V —
 *     see keyboard-shortcuts.config.ts.
 */
export class ImagePasteHandler implements ReactiveController {
    readonly #bus: WhiteboardBus;
    readonly #getStageSize: () => { width: number; height: number };
    readonly #getViewport: () => Viewport;
    readonly #getUploadHandler: () => ImageUploadHandler | undefined;
    readonly #getChoiceHandler: () => PasteChoiceHandler | undefined;
    readonly #boundHandler: (e: Event) => void;
    #connected = false;
    /**
     * Generation of the open text-or-image question, `null` when none is open. A counter
     * rather than a flag: a question still open across a disconnect/reconnect must not
     * clear the flag of a newer one when it finally settles.
     */
    #openChoice: number | null = null;
    #choiceGeneration = 0;

    constructor(
        _host: ReactiveControllerHost,
        bus: WhiteboardBus,
        getStageSize: () => { width: number; height: number },
        getViewport: () => Viewport,
        // Optional so the three-getter construction stays valid; the element
        // wires it so a handler set AFTER construction is still picked up.
        getUploadHandler: () => ImageUploadHandler | undefined = () => undefined,
        getChoiceHandler: () => PasteChoiceHandler | undefined = () => undefined,
    ) {
        this.#bus = bus;
        this.#getStageSize = getStageSize;
        this.#getViewport = getViewport;
        this.#getUploadHandler = getUploadHandler;
        this.#getChoiceHandler = getChoiceHandler;
        this.#boundHandler = (e: Event) => this.#handlePaste(e as ClipboardEvent);
    }

    hostConnected(): void {
        // Guard against double-registration (FC10).
        if (this.#connected) return;
        this.#connected = true;
        window.addEventListener('paste', this.#boundHandler);
    }

    hostDisconnected(): void {
        // Before the guard: an unanswered question must never block pasting for good.
        this.#openChoice = null;
        if (!this.#connected) return;
        this.#connected = false;
        window.removeEventListener('paste', this.#boundHandler);
    }

    #handlePaste(e: ClipboardEvent): void {
        // The paste belongs to an editor (FC11) — see the class comment.
        if (e.defaultPrevented || isEditableTarget(e.target) || isInsideEditable(e.target)) return;

        // Review 0023, Lauf 02: der Guard im Bus greift erst bei
        // `uiActions$.next()` — der Upload unten läuft aber VORHER. Ohne diese
        // Zeile läge nach einem Strg+V auf einem gesperrten Board ein
        // verwaistes Asset auf dem Server, auch wenn das Objekt nie im Board
        // ankommt. Deshalb hier, ganz am Anfang.
        //
        // Was das NICHT abdeckt (Lauf 03, bewusst so entschieden): Der Paste
        // beginnt auf einem offenen Board, der Upload läuft, und erst währenddessen
        // wird gesperrt. Dann ist das Asset schon hochgeladen, und der Bus-Guard
        // verwirft die Platzierung — siehe `#uploadAndDispatch`. Das gesperrte
        // Board gewinnt; der Rest ist ein Speicherrest, kein Schreibzugriff auf
        // fremd gesperrte Daten.
        if (this.#bus.readonly) return;

        // One text-or-image question at a time: while it is open, EVERY Ctrl+V onto the
        // board does nothing (text and copied board objects too) — nothing lands behind the
        // dialog by surprise. Pastes into editors are unaffected (guard above).
        if (this.#openChoice !== null) {
            e.preventDefault();
            return;
        }

        // Read synchronously: `clipboardData` is unreadable after the event.
        const file = this.#firstImageFile(e.clipboardData);
        const text = e.clipboardData?.getData('text/plain') ?? '';
        const ask = this.#getChoiceHandler();
        switch (decidePasteRoute({ text, hasImageFile: file !== null, hasChoiceHandler: ask !== undefined })) {
            case 'board-payload':
                // The board's own marker comes first (Stefan, 2026-10-28): a copied image
                // also carries image/png, and checking the picture first would ask "image or
                // text?" on every board-to-board paste, or re-upload it without its rotation.
                e.preventDefault();
                this.#bus.uiActions$.next({ type: 'clipboard/paste-text', text });
                return;
            case 'ask':
                e.preventDefault();
                void this.#askAndPaste(ask!, file!, text);
                return;
            case 'image':
                e.preventDefault();
                this.#pasteImage(file!);
                return;
            case 'text':
                // Anke, 2026-07-21 — kein Bild: dann entscheidet der TEXT der echten
                // Zwischenablage, nicht mehr blind der board-eigene Spiegel. Genau das
                // erzeugte den Eindruck zweier Ablagen — wer woanders Text kopierte und
                // hier einfuegte, bekam wieder das alte Board-Objekt.
                //
                // Dieses native Ereignis ist die einzige Stelle, an der die Zwischenablage
                // OHNE Erlaubnisfrage synchron lesbar ist; darum laeuft Strg+V bewusst
                // hierueber (siehe keyboard-shortcuts.config.ts).
                e.preventDefault();
                this.#bus.uiActions$.next({ type: 'clipboard/paste-text', text });
                return;
            case 'mirror':
                // Gar nichts Brauchbares in der Ablage → der Spiegel darf es versuchen
                // (No-op, wenn nie etwas kopiert wurde). Koordinaten sind fuer das
                // versatzbasierte Einfuegen ungenutzt.
                this.#bus.uiActions$.next({ type: 'clipboard/paste', worldX: 0, worldY: 0 });
                return;
        }
    }

    /** The first `image/*` item that yields a file (Single-Image-First — FC16). */
    #firstImageFile(data: DataTransfer | null): File | null {
        const items = data?.items;
        if (!items) return null;
        for (let i = 0; i < items.length; i++) {
            const item = items[i]!;
            if (!item.type.startsWith('image/')) continue;
            const file = item.getAsFile();
            if (file) return file;
        }
        return null;
    }

    /** Place a pasted image: via the upload seam when injected, else as a data-URL. */
    #pasteImage(file: File): void {
        const upload = this.#getUploadHandler();
        if (upload) {
            // Fire-and-forget: the paste handler must stay synchronous for the
            // browser, the placement lands once the upload does.
            void this.#uploadAndDispatch(upload, file);
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const dataUrl = (ev.target?.result ?? null) as string | null;
            if (typeof dataUrl !== 'string' || dataUrl.length === 0) return;
            this.#dispatch({ dataUrl });
        };
        reader.onerror = () => {
            console.error('[ImagePasteHandler] FileReader failed to read clipboard image');
        };
        reader.readAsDataURL(file);
    }

    /**
     * Text AND image in the clipboard: ask the host how to paste (Anke, 2026-10-02).
     * Nothing is uploaded or placed before the answer.
     */
    async #askAndPaste(ask: PasteChoiceHandler, file: File, text: string): Promise<void> {
        const generation = ++this.#choiceGeneration;
        this.#openChoice = generation;
        try {
            let choice: PasteChoice | null;
            try {
                choice = await ask();
            } catch (err) {
                console.error('[ImagePasteHandler] paste choice failed — nothing pasted', err);
                return;
            }
            // Locked, left or superseded while the question was open (a disconnect clears
            // `#openChoice`, a reconnect may open a newer question): paste nothing, upload
            // nothing.
            if (this.#openChoice !== generation || this.#bus.readonly) return;
            if (choice === 'image') this.#pasteImage(file);
            else if (choice === 'text') this.#bus.uiActions$.next({ type: 'clipboard/paste-text', text });
        } finally {
            if (this.#openChoice === generation) this.#openChoice = null;
        }
    }

    /**
     * Upload the pasted file, then place the returned asset URL as `src`.
     *
     * A rejected upload places NOTHING — falling back to the base64 data-URL
     * would put into the board exactly what the seam exists to keep out.
     */
    async #uploadAndDispatch(upload: ImageUploadHandler, file: File): Promise<void> {
        let result: { src: string };
        try {
            result = await upload(file);
        } catch (err) {
            console.error('[ImagePasteHandler] image upload failed', err);
            return;
        }
        // Review 0023, Lauf 03: Zwischen dem `await` oben und hier kann das Board
        // gesperrt worden sein. Dann verwirft der Bus-Guard die Platzierung, und
        // das bereits hochgeladene Asset bleibt unreferenziert liegen. Bewusste
        // Entscheidung (Anke): lieber ein Speicherrest als ein Schreibzugriff auf
        // ein Board, dessen Sperre inzwischen jemand anderes hält. Die Warnung
        // macht den Rest auffindbar, statt ihn stillschweigend zu hinterlassen.
        if (this.#bus.readonly) {
            console.warn(
                '[ImagePasteHandler] Board wurde während des Uploads gesperrt — Platzierung verworfen. ' +
                    'Das hochgeladene Bild bleibt unreferenziert:',
                result.src,
            );
            return;
        }
        this.#dispatch({ src: result.src });
    }

    #dispatch(source: ImageSource): void {
        // Decode the bitmap so the image is placed at its NATIVE pixel size
        // (shrunk to fit the viewport, never enlarged, aspect preserved). The
        // world centre is read at dispatch time so a viewport change during the
        // brief decode is reflected. On the upload path the decode reads the
        // RETURNED asset URL, never the base64 blob.
        const url = 'src' in source ? source.src : source.dataUrl;
        const img = new window.Image();
        img.onload = () => this.#place(source, { width: img.naturalWidth, height: img.naturalHeight });
        // Undecodable → square fallback. On the upload path the asset already
        // exists server-side, so dropping the placement would strand it.
        img.onerror = () => this.#place(source, null);
        img.src = url;
    }

    #place(source: ImageSource, natural: { width: number; height: number } | null): void {
        const stage = this.#getStageSize();
        const vp = this.#getViewport();
        const worldX = (stage.width / 2 - vp.x) / vp.scale;
        const worldY = (stage.height / 2 - vp.y) / vp.scale;
        const size = natural
            ? fitImageWorldSize(natural.width, natural.height, stage.width, stage.height, vp.scale)
            : undefined;
        this.#bus.uiActions$.next({
            type: 'place/image',
            worldX,
            worldY,
            // Spread the union so `src` and `dataUrl` stay MUTUALLY EXCLUSIVE —
            // the absent field is absent, not present-but-undefined.
            ...source,
            ...(size ? { size } : {}),
        });
    }
}
